import type { ResourceType } from 'types/common';
type ValueType = string | number | boolean | null;

type FilterParameters = {
  type:
    | 'equal'
    | 'greaterThan'
    | 'lessThan'
    | 'startsWith'
    | 'endsWith'
    | 'contains'
    | 'lessOrEqual';
  key: string;
  value: ValueType;
};

type PaginationQuery = {
  pageNumber: number;
  pageSize: number;
};

const valueWrapper = (value: ValueType) => {
  if (value === null) {
    return value;
  }
  return `%27${value}%27`;
};

const generateCustomFilter = (key: string, value: ValueType | string[]) => {
  if (Array.isArray(value)) {
    const commaSeparatedValues = value.join(',');
    return `custom_filter[${key}]=${commaSeparatedValues}`;
  }
  return `custom_filter[${key}]=${value}`;
};

const generateEqualExpression = (key: string, value: ValueType) => {
  return `equals(${key},${valueWrapper(value)})`;
};

const generateGreaterThanExpression = (key: string, value: ValueType) => {
  return `greaterThan(${key},${valueWrapper(value)})`;
};

const generateLessThanExpression = (key: string, value: ValueType) => {
  return `lessThan(${key},${valueWrapper(value)})`;
};
const generateContainsExpression = (key: string, value: ValueType) => {
  return `contains(${key},${valueWrapper(value)})`;
};
const generateStartsWithExpression = (key: string, value: ValueType) => {
  return `startsWith(${key},${valueWrapper(value)})`;
};
const generateEndsWithExpression = (key: string, value: ValueType) => {
  return `endsWith(${key},${valueWrapper(value)})`;
};
const generateLessThanOrEqualExpression = (key: string, value: ValueType) => {
  return `lessOrEqual(${key},${valueWrapper(value)})`;
};

const getFilterExpressionsByType = (
  type: FilterParameters['type'] = 'equal'
) => {
  switch (type) {
    case 'equal':
      return generateEqualExpression;
    case 'greaterThan':
      return generateGreaterThanExpression;
    case 'lessThan':
      return generateLessThanExpression;
    case 'contains':
      return generateContainsExpression;
    case 'startsWith':
      return generateStartsWithExpression;
    case 'endsWith':
      return generateEndsWithExpression;
    case 'lessOrEqual':
      return generateLessThanOrEqualExpression;
    default:
      return generateEqualExpression;
  }
};

const generateFilterExpression = (
  filter: 'and' | 'or',
  filterParameters: FilterParameters[]
) => {
  const queryStringArray = filterParameters.map(({ key, value, type }) =>
    getFilterExpressionsByType(type)(key, value)
  );
  queryStringArray.join(',');

  return `filter=expr:${filter}(${queryStringArray.join(',')})`;
};

const generateORExpression = (filterParameters: FilterParameters[]) => {
  const queryStringArray = filterParameters.map(({ key, value, type }) =>
    getFilterExpressionsByType(type)(key, value)
  );
  queryStringArray.join(',');

  return `or(${queryStringArray.join(',')})`;
};

const generateCombinedFilterExpressionWithAnd = (
  filterExpression: string[]
) => {
  // This will generate a filter expression by combining all filters (string[]) with AND operator
  // this allows us to generate filters with the combination of and,or,not,equal ets
  return `filter=expr:and(${filterExpression.join(',')})`;
};

const generateFilter = (
  key: string,
  value: ValueType,
  type: FilterParameters['type'] = 'equal'
) => {
  return `filter=expr:${getFilterExpressionsByType(type)(key, value)}`;
};
const generatePaginationQuery = ({ pageNumber, pageSize }: PaginationQuery) => {
  return `page[number]=${pageNumber}&page[size]=${pageSize}`;
};
const generateSortQuery = (sortBy: string, direction: 'Acs' | 'Decs') => {
  if (direction === 'Acs') {
    return `sort=${sortBy}`;
  }

  return `sort=-${sortBy}`;
};

const generateAccessTokenQuery = (accessToken: string) => {
  return `access_token=${accessToken}`;
};

const generateIncludeQuery = (includeItem: string[]) => {
  return `include=${includeItem.join(',')}`;
};

const generateURL = (
  entryPointUlr: ResourceType,
  parameterArray: string[] = [],
  id: string | undefined | null = undefined
) => {
  let url = entryPointUlr as string;

  if (id) {
    url = url + '/' + id;
  }
  if (!parameterArray.length) {
    return url;
  }

  return `${url}?${parameterArray.join('&')}`;
};

const generateAppendQueryParam = (key: string, value: ValueType) => {
  return `${key}=${value}`;
};

export {
  generateURL,
  generateFilter,
  generateSortQuery,
  generateIncludeQuery,
  generatePaginationQuery,
  generateFilterExpression,
  generateAccessTokenQuery,
  generateCustomFilter,
  generateAppendQueryParam,
  generateEqualExpression,
  generateGreaterThanExpression,
  generateLessThanExpression,
  generateContainsExpression,
  generateStartsWithExpression,
  generateEndsWithExpression,
  generateLessThanOrEqualExpression,
  generateCombinedFilterExpressionWithAnd,
  generateORExpression,
  type FilterParameters,
  type PaginationQuery,
};
